import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '../ui/Spinner';
import { TaskEditModal } from '../tasks/TaskEditModal';
import { useTaskListPreview } from '../tasks/useTaskListPreview';
import { HearingModal } from './HearingModal';
import { useEffectiveDossierFields } from '../../hooks/queries/useCabinetSettings';
import { AgendaPreviewCard } from './AgendaPreviewCard';
import { AppointmentDialog } from '../calendar/AppointmentDialog';
import {
  TASK_FILTERS,
  useTasks,
  useTaskSections,
} from '../../hooks/queries/useTasks';
import { useTasksAssignments } from '../../hooks/queries/useTaskCollaboration';
import { useTasksStore } from '../../store/tasksStore';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import type { Dossier } from '../../services/dossiers/dossierService';
import { useDossierHearings, useHearingsAttendees } from '../../hooks/queries/useDossierHearings';
import { useDossierAppointments } from '../../hooks/queries/useAppointments';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useDossierContacts } from '../../hooks/queries/useContacts';
import { getContactDisplayName } from '../../services/contacts/contactService';
import {
  aggregateDossierCalendarEvents,
  resolveDossierCalendarClick,
  type DossierCalendarEvent,
  type DossierCalendarEventKind,
} from '../../services/calendar/dossierCalendar';
import { buildAgendaRows } from '../../services/calendar/agendaTable';
import { cn } from '../../lib/cn';
import { startOfMonth } from 'date-fns';
import { ListToolbar, Segments, ToolbarGrow } from '../ui/ListToolbar';
import { FaCalendar, FaList, FaPlus } from '../ui/FaIcons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { TaskCreateModal } from '../tasks/TaskCreateModal';
import { NextHearingBand } from './DossierOverviewBlocks';
import { CalendarQuickCreate, type QuickCreateSlot } from '../calendar/CalendarQuickCreate';
import { AGENDA_KIND_DOT, AgendaList, AgendaMiniCalendar, AgendaMonth } from './DossierAgendaViews';

interface Props {
  dossier: Dossier;
}

const AGENDA_FILTERS: { kind: DossierCalendarEventKind; label: string }[] = [
  { kind: 'hearing', label: 'Audiences' },
  { kind: 'appointment', label: 'Rendez-vous' },
  { kind: 'task', label: 'Tâches' },
  { kind: 'prescription', label: 'Prescription' },
];

export function DossierAgendaTab({ dossier }: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingDeleteIds = useTasksStore((s) => s.pendingDeleteIds);
  const canEdit = useCanAccessFeature('hearings_create');
  const { data: tasks = [], isLoading, isError } = useTasks({
    ...TASK_FILTERS.ACTIVE_ONLY,
    dossier_id: dossier.id,
  });
  const { data: hearings = [] } = useDossierHearings(dossier.id);
  const { data: appointments = [] } = useDossierAppointments(dossier.id);
  const { data: sections = [] } = useTaskSections(dossier.id);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [hearingForm, setHearingForm] = useState<'new' | string | null>(null);
  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const hearingFromUrl = searchParams.get('hearing');
  const appointmentFromUrl = searchParams.get('appointment');
  const [view, setView] = useState<'list' | 'month'>('list');
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [hiddenKinds, setHiddenKinds] = useState<Set<DossierCalendarEventKind>>(() => new Set());
  const [appointmentNew, setAppointmentNew] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const [slot, setSlot] = useState<QuickCreateSlot | null>(null);
  const canCreateTask = useCanAccessFeature('tasks_create');

  const visibleTasks = useMemo(
    () => tasks.filter((t) => !pendingDeleteIds.has(t.id)),
    [tasks, pendingDeleteIds],
  );

  const { prescription: showPrescription } = useEffectiveDossierFields();
  const events = useMemo(
    () =>
      aggregateDossierCalendarEvents(
        {
          id: dossier.id,
          next_hearing_at: dossier.next_hearing_at,
          prescription_date: showPrescription ? dossier.prescription_date : null,
        },
        visibleTasks,
        hearings,
        appointments,
      ),
    [dossier.id, dossier.next_hearing_at, dossier.prescription_date, showPrescription, visibleTasks, hearings, appointments],
  );

  const hearingIds = useMemo(() => hearings.map((hearing) => hearing.id), [hearings]);
  const { data: attendees = [] } = useHearingsAttendees(hearingIds);
  const { data: collaborators = [] } = useCollaborators();
  const { data: linked = [] } = useDossierContacts(dossier.id);
  const taskIds = useMemo(
    () => events.flatMap((event) => (event.kind === 'task' && event.taskId ? [event.taskId] : [])),
    [events],
  );
  const { data: assignments } = useTasksAssignments(taskIds);

  const rows = useMemo(() => {
    const contacts = new Map<string, string>();
    for (const row of linked) contacts.set(row.contact_id, getContactDisplayName(row.contact));
    return buildAgendaRows(events, {
      hearings: hearings.map((hearing) => ({
        id: hearing.id,
        description: hearing.description,
        court: hearing.court,
        notes: hearing.notes,
        ownerId: hearing.owner_id,
      })),
      attendees: attendees.map((person) => ({
        hearingId: person.hearing_id,
        collaboratorId: person.collaborator_id,
        contactId: person.contact_id,
      })),
      appointments: appointments.map((item) => ({
        id: item.id,
        participantIds: item.participantIds,
      })),
      tasks: visibleTasks.map((task) => ({
        id: task.id,
        description: task.description,
        assigneeIds: (assignments?.get(task.id) ?? []).map((person) => person.id),
      })),
      collaborators: new Map(collaborators.map((person) => [person.id, person.full_name ?? ''])),
      contacts,
    });
  }, [appointments, assignments, attendees, collaborators, events, hearings, linked, visibleTasks]);

  const rowById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const now = useMemo(() => new Date(), []);
  useEffect(() => {
    if (appointmentFromUrl) setAppointmentId(appointmentFromUrl);
  }, [appointmentFromUrl]);

  const editingTask = useMemo(
    () => (editingTaskId ? visibleTasks.find((t) => t.id === editingTaskId) ?? null : null),
    [editingTaskId, visibleTasks],
  );

  const editingHearing = useMemo(
    () =>
      typeof hearingForm === 'string'
        ? hearings.find((h) => h.id === hearingForm) ?? null
        : null,
    [hearingForm, hearings],
  );

  function onEventClick(event: DossierCalendarEvent) {
    const click = resolveDossierCalendarClick(event);
    if (!click) return;
    if (click.action === 'openTask') {
      setEditingTaskId(click.taskId);
      return;
    }
    if (click.action === 'openHearing') {
      setHearingForm(click.hearingId);
      return;
    }
    if (click.action === 'openAppointment') {
      setAppointmentId(click.appointmentId);
      setSearchParams((prev) => {
        const sp = new URLSearchParams(prev);
        sp.set('appointment', click.appointmentId);
        return sp;
      }, { replace: true });
      return;
    }
    navigate(click.href);
  }

  function closeAppointment() {
    setAppointmentId(null);
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('appointment');
      return sp;
    }, { replace: true });
  }

  const preview = useTaskListPreview(hearingForm !== null || appointmentId !== null || editingTaskId !== null);
  const hovered = preview.hoverId ? rowById.get(preview.hoverId) ?? null : null;

  const shownEvents = events.filter((event) => !hiddenKinds.has(event.kind));
  const kindsPresent = AGENDA_FILTERS.filter((filter) => filter.kind !== 'prescription' || showPrescription);

  function toggleKind(kind: DossierCalendarEventKind) {
    setHiddenKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  return (
    <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="flex min-w-0 flex-col gap-3.5">
        <NextHearingBand dossier={dossier} />

        <ListToolbar>
          {kindsPresent.map((filter) => {
            const on = !hiddenKinds.has(filter.kind);
            return (
              <button
                key={filter.kind}
                type="button"
                aria-pressed={on}
                onClick={() => toggleKind(filter.kind)}
                className={cn(
                  'inline-flex h-8 items-center gap-2 rounded-full px-2.5 text-[13px] transition-colors',
                  on ? 'text-cime-text-primary' : 'text-neutral-400',
                )}
              >
                <i aria-hidden className={cn('h-2 w-2 rounded-full', on ? AGENDA_KIND_DOT[filter.kind] : 'bg-neutral-300')} />
                {filter.label}
              </button>
            );
          })}
          <ToolbarGrow />
          <Segments
            label="Affichage"
            value={view}
            onChange={setView}
            options={[
              { value: 'list', label: <><FaList className="h-3 w-3" aria-hidden /> Liste</> },
              { value: 'month', label: <><FaCalendar className="h-3 w-3" aria-hidden /> Mois</> },
            ]}
          />
          {canEdit || canCreateTask ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full bg-cime-primary px-3.5 text-[13px] font-medium text-white transition-[background-color,box-shadow] hover:bg-[#0b4d3d] hover:shadow-[0_2px_8px_rgba(4,52,41,0.28)] active:bg-[#032a21] data-[state=open]:bg-[#0b4d3d]"
                >
                  <FaPlus className="h-3 w-3" aria-hidden /> Ajouter
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[100] min-w-44 rounded-cime border-neutral-200 bg-white">
                {canEdit ? (
                  <DropdownMenuItem onSelect={() => setHearingForm('new')}>Une audience</DropdownMenuItem>
                ) : null}
                {canEdit ? (
                  <DropdownMenuItem onSelect={() => setAppointmentNew(true)}>Un rendez-vous</DropdownMenuItem>
                ) : null}
                {canCreateTask ? (
                  <DropdownMenuItem onSelect={() => setTaskCreateOpen(true)}>Une tâche</DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </ListToolbar>

        {isError ? (
          <p className="py-10 text-center text-sm text-neutral-400">Agenda indisponible</p>
        ) : isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : view === 'list' ? (
          <AgendaList
            events={shownEvents}
            now={now}
            highlightId={hearingFromUrl}
            onEventClick={onEventClick}
            onEnter={preview.onEnter}
            onLeave={preview.onLeave}
          />
        ) : (
          <AgendaMonth
            events={shownEvents}
            now={now}
            month={month}
            onMonthChange={setMonth}
            onDay={canEdit || canCreateTask ? (day, x, y) => setSlot({ day, time: null, x, y }) : undefined}
            onEventClick={onEventClick}
            onEnter={preview.onEnter}
            onLeave={preview.onLeave}
          />
        )}
      </div>

      <AgendaMiniCalendar events={shownEvents} now={now} month={month} onMonthChange={setMonth} />

      {preview.rect && hovered && (
        <AgendaPreviewCard row={hovered} anchor={preview.rect} shown={preview.shown} />
      )}
      <TaskEditModal
        task={editingTask}
        sections={sections}
        onClose={() => setEditingTaskId(null)}
      />
      <HearingModal
        open={hearingForm !== null}
        dossierId={dossier.id}
        hearing={editingHearing}
        onClose={() => setHearingForm(null)}
      />
      <AppointmentDialog
        open={appointmentId !== null || appointmentNew}
        appointmentId={appointmentId}
        contextDossierId={dossier.id}
        onClose={() => {
          setAppointmentNew(false);
          closeAppointment();
        }}
      />
      {slot ? (
        <CalendarQuickCreate
          key={`${slot.day}-${slot.x}-${slot.y}`}
          slot={slot}
          fixedDossier={{ id: dossier.id, title: dossier.title, number: dossier.number }}
          onClose={() => setSlot(null)}
          onMore={(kind) => {
            setSlot(null);
            if (kind === 'hearing') setHearingForm('new');
            else if (kind === 'appointment') setAppointmentNew(true);
            else setTaskCreateOpen(true);
          }}
        />
      ) : null}
      <TaskCreateModal
        open={taskCreateOpen}
        onClose={() => setTaskCreateOpen(false)}
        dossierId={dossier.id}
      />
    </div>
  );
}
